import React, { useState, useCallback } from "react";
import {
  Form, Button, Card, Typography, Spin, Modal, Row, Col, Steps, Tag,
} from "antd";
import { LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import { PaymentTerm } from "../../types/invoice";
import InvoicePreview from "./InvoicePreview";
import InvoiceCostOverview from "./InvoiceCostOverview";
import InvoiceWizardStep1Tariff from "./InvoiceWizardStep1Tariff";
import InvoiceWizardStep4Review from "./InvoiceWizardStep4Review";
import { CustomsSection, LMDSection } from "./form-sections";
import { useInvoiceForm } from "./useInvoiceForm";
import {
  InvoiceFormProps,
  getSecondsAgo,
  getBookingDisplayName,
  hasDeliveryInvoiceLines,
} from "./invoiceFormHelpers";

const { Text } = Typography;

const WIZARD_STEPS = [
  { title: "Tariff" },
  { title: "Customs" },
  { title: "Delivery" },
  { title: "Review" },
];

const DELIVERY_STEP_INDEX = 2;
const REVIEW_STEP_INDEX = 3;

const InvoiceForm: React.FC<InvoiceFormProps> = (props) => {
  const hook = useInvoiceForm(props);
  const [currentStep, setCurrentStep] = useState(0);

  const proceedToStep = useCallback(async (nextStep: number) => {
    if (!hook.selectedBooking?.id) {
      return;
    }

    // Leaving Tariff (step 0) → create draft with booking_id + company_id
    if (currentStep === 0 && nextStep >= 1 && !hook.activeInvoice?.id) {
      const ok = await hook.createDraft();
      if (!ok) return;
    }
    setCurrentStep(nextStep);
  }, [hook, currentStep]);

  const handleStepChange = useCallback(async (nextStep: number) => {
    if (!hook.selectedBooking) return;

    if (nextStep <= currentStep) {
      setCurrentStep(nextStep);
      return;
    }

    const movingPastDelivery =
      nextStep >= REVIEW_STEP_INDEX && currentStep <= DELIVERY_STEP_INDEX;
    const lines = hook.formValues.lines || [];

    if (movingPastDelivery && !hasDeliveryInvoiceLines(lines)) {
      Modal.confirm({
        title: "No delivery lines added",
        content:
          "You have not added any Last Mile Delivery (LMD) lines to this invoice. Continue to review without delivery costs?",
        okText: "Continue without delivery",
        cancelText: "Stay on Delivery",
        onOk: () => proceedToStep(nextStep),
      });
      return;
    }

    await proceedToStep(nextStep);
  }, [hook, currentStep, proceedToStep]);

  if (hook.initialDataLoading) {
    return (
      <Card title={hook.isEditMode ? "Edit Invoice" : "Create Invoice"} size="small" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400, flexDirection: "column", gap: 16 }}>
          <Spin size="large" />
          <Text type="secondary">Loading invoice data...</Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>
              {hook.editInvoice ? "Edit Invoice" : "Create Invoice"}
              {hook.draftInvoice && !hook.editInvoice && (
                <Tag color="blue" style={{ marginLeft: 8, verticalAlign: "middle", fontSize: 11 }}>DRAFT</Tag>
              )}
              {hook.selectedBooking && (
                <Text type="secondary" style={{ fontWeight: 400, fontSize: 13, marginLeft: 10 }}>
                  — {getBookingDisplayName(hook.selectedBooking).split(" - ")[0]}
                  {" · "}
                  {(() => {
                    const b = hook.selectedBooking;
                    const company = b.company_info
                      ? Array.isArray(b.company_info)
                        ? b.company_info[0]?.name || ""
                        : b.company_info.name || ""
                      : "";
                    return company;
                  })()}
                </Text>
              )}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button type="default" onClick={() => hook.setPreviewModalVisible(true)} disabled={!hook.selectedBooking}>
                Preview Invoice
              </Button>
              {hook.activeInvoice?.id && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {hook.autoSaving ? (
                    <span style={{ color: "#1890ff" }}><LoadingOutlined style={{ marginRight: 4 }} />Saving...</span>
                  ) : hook.lastSaved ? (
                    <span style={{ color: "#52c41a" }}>
                      ✓ Saved{" "}
                      {(() => {
                        const s = getSecondsAgo(hook.lastSaved);
                        if (s < 60) return `${s} seconds ago`;
                        if (s < 3600) return `${Math.floor(s / 60)} minutes ago`;
                        return hook.lastSaved.toLocaleTimeString();
                      })()}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        }
        size="small"
        style={{ width: "100%" }}
      >
        <Form
          form={hook.form}
          layout="vertical"
          onFinish={hook.handleSubmit}
          initialValues={{
            payment_term: PaymentTerm.DAYS_30,
            lines: [{ description: "", quantity: 1, unit_price: 0, vat_percentage: 0 }],
          }}
          onValuesChange={hook.handleValuesChange}
          size="small"
        >
          <Row gutter={20}>
            <Col span={15}>
              <Steps
                current={currentStep}
                onChange={handleStepChange}
                size="small"
                style={{ marginBottom: 16 }}
                items={WIZARD_STEPS}
              />

              {currentStep === 0 && (
                <InvoiceWizardStep1Tariff
                  form={hook.form}
                  selectedBooking={hook.selectedBooking}
                  bookings={hook.bookings}
                  isEditMode={hook.isEditMode}
                  initialBookingId={hook.initialBookingId}
                  formValues={hook.formValues}
                  setFormValues={hook.setFormValues}
                  tariffCalculationType={hook.tariffCalculationType}
                  setTariffCalculationType={hook.setTariffCalculationType}
                  tariffRow={hook.tariffRow}
                  setTariffRow={hook.setTariffRow}
                  suggestedTariffs={hook.suggestedTariffs}
                  setSuggestedTariffs={hook.setSuggestedTariffs}
                  loadingSuggestions={hook.loadingSuggestions}
                  selectedTariffId={hook.selectedTariffId}
                  setSelectedTariffId={hook.setSelectedTariffId}
                  containerSizes={hook.containerSizes}
                  handleBookingChange={hook.handleBookingChange}
                  addTariffToInvoiceLines={hook.addTariffToInvoiceLines}
                  fetchTariffSuggestions={hook.fetchTariffSuggestions}
                  isMatchingContainer={hook.isMatchingContainer}
                  autogenerateInvoiceLines={hook.autogenerateInvoiceLines}
                  autogeneratingLines={hook.autogeneratingLines}
                  autoSaveInvoice={hook.autoSaveInvoice}
                  getBookingDisplayName={getBookingDisplayName}
                />
              )}

              {currentStep === 1 && (
                <CustomsSection
                  selectedBooking={hook.selectedBooking}
                  onAddCustomsLines={hook.addCustomsToInvoiceLines}
                  existingCostTypes={(hook.formValues.lines || []).map((l) => l.cost_type).filter(Boolean) as string[]}
                  onDeclarationLinked={hook.applyLinkedDeclaration}
                />
              )}

              {currentStep === 2 && (
                <LMDSection
                  selectedBooking={hook.selectedBooking}
                  onAddLMDLine={hook.addLMDToInvoiceLines}
                  onTransportPalletCountChange={hook.syncOutboundPalletQuantities}
                  onTransportShipmentFeesChange={hook.syncOutboundShipmentFees}
                />
              )}

              {currentStep === 3 && (
                <InvoiceWizardStep4Review
                  form={hook.form}
                  selectedBooking={hook.selectedBooking}
                  formValues={hook.formValues}
                  setFormValues={hook.setFormValues}
                  pricingComponents={hook.pricingComponents}
                  isEditMode={hook.isEditMode}
                  isExistingInvoice={!!hook.editInvoice}
                  loading={hook.loading}
                  autoSaving={hook.autoSaving}
                  lastSaved={hook.lastSaved}
                  savingLineIndex={hook.savingLineIndex}
                  autogeneratingLines={hook.autogeneratingLines}
                  autoSaveInvoiceLine={hook.autoSaveInvoiceLine}
                  autoSaveInvoice={hook.autoSaveInvoice}
                  onCancel={hook.onCancel}
                  onCompanyPaymentTermUpdated={hook.applyCompanyPaymentTerm}
                />
              )}

              {/* Step navigation */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <Button
                  disabled={currentStep === 0}
                  onClick={() => handleStepChange(currentStep - 1)}
                  size="small"
                >
                  Back
                </Button>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {hook.draftInvoice && !hook.editInvoice && (
                    <Tag icon={<SaveOutlined />} color="blue" style={{ margin: 0 }}>
                      Draft saved
                    </Tag>
                  )}
                  {currentStep < WIZARD_STEPS.length - 1 && (
                    <Button
                      type="primary"
                      onClick={() => handleStepChange(currentStep + 1)}
                      size="small"
                      disabled={!hook.selectedBooking}
                      loading={hook.autoSaving && !hook.activeInvoice?.id}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </div>
            </Col>

            <Col span={9}>
              <InvoiceCostOverview
                lines={hook.formValues.lines || []}
                loading={hook.autogeneratingLines}
                booking={hook.selectedBooking}
                wizardStep={currentStep}
                hasLmdLines={hasDeliveryInvoiceLines(hook.formValues.lines || [])}
                onProductsHsUpdated={hook.applyProductsHsUpdated}
                onRemoveLine={(index) => {
                  const current = hook.form.getFieldValue("lines") || [];
                  const updated = current.filter((_: unknown, i: number) => i !== index);
                  hook.form.setFieldsValue({ lines: updated });
                  hook.setFormValues((prev) => ({ ...prev, lines: updated }));
                }}
              />
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Invoice Preview Modal */}
      <Modal
        title="Invoice Preview"
        open={hook.previewModalVisible}
        onCancel={() => hook.setPreviewModalVisible(false)}
        footer={[<Button key="close" onClick={() => hook.setPreviewModalVisible(false)}>Close</Button>]}
        width={800}
        style={{ top: 20 }}
      >
        <InvoicePreview invoice={hook.formValues} booking={hook.selectedBooking} />
      </Modal>

      {/* Pricing Information Modal */}
      <Modal
        title="How Pricing Components Work"
        open={hook.pricingInfoVisible}
        onCancel={() => hook.setPricingInfoVisible(false)}
        footer={[<Button key="close" size="small" onClick={() => hook.setPricingInfoVisible(false)}>Close</Button>]}
        width={600}
      >
        <div style={{ maxHeight: 400, overflow: "auto" }}>
          <h4>Pricing Calculation Process</h4>
          <p>When you select a booking, the system automatically generates invoice lines by:</p>
          <ol>
            <li>
              <strong>Matching pricing rules</strong> based on transport type, origin/destination ports, shipping method, container size, and date validity.
            </li>
            <li>
              <strong>Calculating quantities</strong> based on component type: per_kg (weight), per_cbm (volume), per_product, per_unique_product, per_carton, or fixed rate.
            </li>
            <li>
              <strong>Grouping components</strong> by type: freight charges, handling fees, documentation, customs clearance, and other services.
            </li>
          </ol>
          <h4>Manual Adjustments</h4>
          <p>
            You can manually adjust any generated line item or add new ones. Use the &quot;Regenerate Lines from Pricing Rules&quot; button to recalculate.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default InvoiceForm;
